import React, { useState, useEffect } from 'react';
import {
  ExclamationTriangleIcon,
  CheckCircleIcon,
  ClockIcon,
  DocumentTextIcon,
  InformationCircleIcon,
  ChevronDownIcon,
  ChevronUpIcon,
} from '@heroicons/react/24/outline';
import { getSignalOmbudsmanAdvice } from '../../utils/api';
import { OmbudsmanAdvice, SimilarOmbudsmanDecision } from '../../types/ombudsman';
import { Panel } from '../v3/common/Panel';
import { Badge } from '../ui/badge';
import { Timestamp } from '../ui/timestamp';

interface OmbudsmanAdvicePanelProps {
  signalId: string;
}

const PANEL_TITLE = 'Ombudsman Advisory Service';

const OmbudsmanAdvicePanel: React.FC<OmbudsmanAdvicePanelProps> = ({ signalId }) => {
  const [advice, setAdvice] = useState<OmbudsmanAdvice | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expandedDecision, setExpandedDecision] = useState<string | null>(null);

  useEffect(() => {
    const loadAdvice = async () => {
      setLoading(true);
      setError(null);
      
      try {
        const response = await getSignalOmbudsmanAdvice(signalId);
        setAdvice(response);
      } catch (err) {
        console.error('Error loading ombudsman advice:', err);
        setError('Failed to load ombudsman advice');
      } finally {
        setLoading(false);
      }
    };

    if (signalId) {
      loadAdvice();
    }
  }, [signalId]);

  const getConfidenceIcon = (confidence: string) => {
    switch (confidence) {
      case 'high':
        return <ExclamationTriangleIcon className="h-5 w-5 text-red-500" />;
      case 'medium':
        return <ClockIcon className="h-5 w-5 text-yellow-500" />;
      case 'low':
        return <InformationCircleIcon className="h-5 w-5 text-blue-500" />;
      default:
        return <DocumentTextIcon className="h-5 w-5 text-gray-500" />;
    }
  };

  const getConfidenceColor = (confidence: string) => {
    switch (confidence) {
      case 'high':
        return 'bg-red-50 border-red-200 text-red-800';
      case 'medium':
        return 'bg-yellow-50 border-yellow-200 text-yellow-800';
      case 'low':
        return 'bg-blue-50 border-blue-200 text-blue-800';
      default:
        return 'bg-gray-50 border-gray-200 text-gray-800';
    }
  };

  const getDecisionStatusIcon = (status: string) => {
    switch (status) {
      case 'Upheld':
        return <ExclamationTriangleIcon className="h-4 w-4 text-red-500" />;
      case 'Not Upheld':
        return <CheckCircleIcon className="h-4 w-4 text-green-500" />;
      case 'Partially Upheld':
        return <ClockIcon className="h-4 w-4 text-yellow-500" />;
      default:
        return <DocumentTextIcon className="h-4 w-4 text-gray-500" />;
    }
  };

  const getDecisionStatusColor = (status: string) => {
    switch (status) {
      case 'Upheld':
        return 'bg-red-100 text-red-800';
      case 'Not Upheld':
        return 'bg-green-100 text-green-800';
      case 'Partially Upheld':
        return 'bg-yellow-100 text-yellow-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const formatAdviceText = (text: string) => {
    // Split by double newlines to create paragraphs
    const paragraphs = text.split('\n\n');
    
    return paragraphs.map((paragraph, index) => {
      // Check if this is a bullet point section
      if (paragraph.includes('• ')) {
        const [title, ...bulletPoints] = paragraph.split('• ').filter(Boolean);
        return (
          <div key={index} className="mb-4">
            {title.trim() && (
              <p className="font-medium text-gray-900 mb-2">{title.trim()}</p>
            )}
            <ul className="list-disc list-inside space-y-1 text-gray-700">
              {bulletPoints.map((point, pointIndex) => (
                <li key={pointIndex}>{point.trim()}</li>
              ))}
            </ul>
          </div>
        );
      } else {
        // Regular paragraph with potential formatting
        const formattedText = paragraph
          .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
          .replace(/⚠️/g, '<span class="text-red-600">⚠️</span>')
          .replace(/ℹ️/g, '<span class="text-blue-600">ℹ️</span>');
        
        return (
          <p 
            key={index} 
            className="mb-3 text-gray-700"
            dangerouslySetInnerHTML={{ __html: formattedText }}
          />
        );
      }
    });
  };

  if (loading) {
    return (
      <Panel title={PANEL_TITLE} className="mb-6">
        <div className="flex items-center justify-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          <span className="ml-3 text-gray-600">Loading ombudsman advice...</span>
        </div>
      </Panel>
    );
  }

  if (error) {
    return (
      <Panel title={PANEL_TITLE} className="mb-6">
        <div className="text-center py-8">
          <ExclamationTriangleIcon className="mx-auto h-12 w-12 text-red-400 mb-4" />
          <p className="text-gray-600">{error}</p>
        </div>
      </Panel>
    );
  }

  if (!advice || advice.similar_decisions.length === 0) {
    return (
      <Panel title={PANEL_TITLE} className="mb-6">
        <div className="text-center py-8">
          <InformationCircleIcon className="mx-auto h-12 w-12 text-gray-400 mb-4" />
          <p className="text-gray-600">No similar ombudsman decisions found relevant to this signal.</p>
        </div>
      </Panel>
    );
  }

  return (
    <Panel title={PANEL_TITLE} className="mb-6">
      <div className="bg-vanilla rounded-lg p-6">
        {/* Header with confidence */}
        <div className="flex items-center gap-2 mb-6">
          <DocumentTextIcon className="h-6 w-6 text-blue-600" />
          <div className="flex items-center gap-2 ml-auto">
            {getConfidenceIcon(advice.confidence)}
            <Badge
              variant="outline"
              className={`${getConfidenceColor(advice.confidence)}`}
              aria-label={`${advice.confidence} confidence`}
            >
              {advice.confidence} confidence
            </Badge>
          </div>
        </div>

        {/* Advice Text */}
        <div className="mb-6 p-4 bg-blue-50 rounded-lg border border-blue-200">
          <div className="prose prose-sm max-w-none">
            {formatAdviceText(advice.advice)}
          </div>
        </div>

        {/* Similar Decisions */}
        <div>
          <h4 className="text-sm font-medium text-gray-900 mb-3">
            Similar Ombudsman Decisions ({advice.similar_decisions.length})
          </h4>
          <div className="space-y-3">
            {advice.similar_decisions.map((decision) => (
              <div key={decision.decision_id} className="border border-gray-200 rounded-lg bg-white">
                <button
                  type="button"
                  className="p-4 w-full text-left hover:bg-gray-50 cursor-pointer"
                  aria-expanded={expandedDecision === decision.decision_id}
                  onClick={() =>
                    setExpandedDecision(
                      expandedDecision === decision.decision_id ? null : decision.decision_id
                    )
                  }
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      {getDecisionStatusIcon(decision.decision_status)}
                      <div>
                        <div className="font-medium text-gray-900">{decision.reference}</div>
                        <div className="text-sm text-gray-600">
                          {decision.industry_sector} • <Timestamp value={decision.decision_date} format="date-only" />
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <Badge
                        variant="outline"
                        className={`${getDecisionStatusColor(decision.decision_status)}`}
                      >
                        {decision.decision_status}
                      </Badge>
                      <div className="text-sm text-gray-600">
                        {(decision.similarity_score * 100).toFixed(0)}% match
                      </div>
                      {expandedDecision === decision.decision_id ? (
                        <ChevronUpIcon className="h-5 w-5 text-gray-400" />
                      ) : (
                        <ChevronDownIcon className="h-5 w-5 text-gray-400" />
                      )}
                    </div>
                  </div>
                </button>
                
                {expandedDecision === decision.decision_id && (
                  <div className="px-4 pb-4 border-t border-gray-100">
                    <div className="pt-3">
                      <h5 className="text-sm font-medium text-gray-900 mb-2">Summary</h5>
                      <p className="text-sm text-gray-700 mb-3">{decision.summary}</p>
                      
                      <div className="flex items-center gap-2">
                        <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${getConfidenceColor(decision.confidence_level)}`}>
                          {decision.confidence_level} confidence
                        </span>
                        <span className="text-xs text-gray-500">
                          Similarity: {(decision.similarity_score * 100).toFixed(1)}%
                        </span>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Footer */}
        <div className="mt-6 pt-4 border-t border-gray-200">
          <p className="text-xs text-gray-500">
            This analysis is based on historical ombudsman decisions with similar complaint patterns.
            Use this information to guide proactive resolution strategies and reduce escalation risk.
          </p>
        </div>
      </div>
    </Panel>
  );
};

export default OmbudsmanAdvicePanel;
